export const UnsupportedBrowserReasonType = Object.freeze({
  VERSION: "version",
  FEATURE: "features",
  OTHER: "other"
});

export default function isSupportedBrowser(device) {
  const isAndroid = device.isAndroid();
  const isIos = device.isIos();
  const browser = device.getBrowser();
  const engine = device.getBrowserEngine();
  const osVersion = device.getOSVersion();
  let majorOSVersion, minorOSVersion;
  if (osVersion) {
    [majorOSVersion, minorOSVersion] = osVersion.split(".").map(Number);
  }
  const engineVersion = device.getBrowserEngineVersion();
  let majorEngineVersion;
  if (engineVersion) {
    majorEngineVersion = engineVersion.split(".").map(Number)[0];
  }
  const isWechat = device.isWechat();

  if (!hasBrowserRequiredFeatures()) {
    return {
      supported: false,
      reason: UnsupportedBrowserReasonType.FEATURE
    };
  }

  const minAndroidVersion = isWechat ? 11 : undefined;
  const minimumChromiumVersion = 92;
  const minIosMajorVersion = isWechat ? 17 : 15;
  const minIosMinorVersion = isWechat ? 0 : 4;
  const unsupportedAndroidBrowsers = [
    "android browser",
    "avast secure browser",
    "avg secure browser",
    "bolt",
    "firefox",
    "maxthon",
    "midori",
    "gobrowser",
    "puffin",
    "ucbrowser",
    "waterfox",
    "zalo"
  ];

  const unsupportedEngines = ["gecko", "trident"];

  if (isAndroid) {
    if (isWechat) {
      if (!osVersion) {
        return {
          supported: false,
          reason: UnsupportedBrowserReasonType.VERSION
        };
      }

      return {
        supported: majorOSVersion > minAndroidVersion,
        reason: UnsupportedBrowserReasonType.VERSION
      };
    }

    // Check if browser name contains any unsupported browser string
    let isKnownUnsupportedBrowser = false;
    for (const unsupported of unsupportedAndroidBrowsers) {
      if (browser.includes(unsupported)) {
        isKnownUnsupportedBrowser = true;
        break;
      }
    }

    if (isKnownUnsupportedBrowser || unsupportedEngines.includes(engine)) {
      return {
        supported: false,
        reason: UnsupportedBrowserReasonType.OTHER
      };
    }

    if (engine === "blink") {
      return {
        supported: majorEngineVersion >= minimumChromiumVersion,
        reason: UnsupportedBrowserReasonType.VERSION
      };
    }

    return {
      supported: false,
      reason: UnsupportedBrowserReasonType.OTHER
    };
  }

  if (isIos) {
    if (!osVersion) {
      return {
        supported: false,
        reason: UnsupportedBrowserReasonType.VERSION
      };
    }

    return {
      supported: majorOSVersion > minIosMajorVersion || (majorOSVersion === minIosMajorVersion && minorOSVersion >= minIosMinorVersion),
      reason: UnsupportedBrowserReasonType.VERSION
    };
  }

  return {
    supported: false,
    reason: UnsupportedBrowserReasonType.OTHER
  };
}

export function hasSharedArrayBuffer() {
  return typeof SharedArrayBuffer !== "undefined";
}

export function hasBrowserRequiredFeatures() {
  return (
    "mediaDevices" in navigator &&
    "enumerateDevices" in navigator.mediaDevices &&
    "getUserMedia" in navigator.mediaDevices &&
    "getSupportedConstraints" in navigator.mediaDevices &&
    "localStorage" in window
  );
}

export function shouldUseBrowserCache(browser) {
  // Disable cache for wechat, samsung browser and opera mobile browser because the deviceID always change
  // https://whatmyuseragent.com/browser/sb/samsung-browser
  // Samsung Browser user agent - Mozilla/5.0 (Linux; Android 11; WP17) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/23.0 Chrome/115.0.0.0 Mobile Safari/537.36
  // Opera Mobile Browser user agent - Mozilla/5.0 (Linux; Android 11; SM-A032F) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/97.0.4692.980 Mobile Safari/537.36 OPR/99.9.9999.99999

  if (!window.localStorage) {
    return false;
  }

  if (!browser) {
    return false;
  }

  const noCacheBrowsers = new Set(["duckduckgo", "huawei", "opera", "samsung", "wechat", "webview"]);

  let isKnownNoCacheBrowser = false;
  for (const noCacheBrowser of noCacheBrowsers) {
    if (browser.includes(noCacheBrowser)) {
      isKnownNoCacheBrowser = true;
      break;
    }
  }

  return !isKnownNoCacheBrowser;
}

export function isLandscapeOrientation() {
  if (screen.orientation?.type.includes("landscape")) {
    return true;
  } else if (window.orientation !== undefined) {
    return Math.abs(window.orientation) === 90;
  } else {
    return window.innerWidth > window.innerHeight;
  }
}

export function onBrowserOrientationChange(callback) {
  const orientationChangeCallback = () => {
    callback(isLandscapeOrientation());
  };

  let removeListener = () => {};

  if ("orientation" in screen && screen.orientation !== undefined && screen.orientation.addEventListener !== undefined) {
    screen.orientation.addEventListener("change", orientationChangeCallback);
    removeListener = () => screen.orientation.removeEventListener("change", orientationChangeCallback);
  } else if ("onorientationchange" in window) {
    window.addEventListener("orientationchange", orientationChangeCallback);
    removeListener = () => window.removeEventListener("orientationchange", orientationChangeCallback);
  } else {
    window.addEventListener("resize", orientationChangeCallback);
    removeListener = () => window.removeEventListener("resize", orientationChangeCallback);
  }

  return removeListener;
}
